import React, { useRef, useEffect } from 'react';
import Box from '@mui/system/Box';
import { InputBase } from '@mui/material';
import styled from '@mui/system/styled';

const CodeInputBase = styled(InputBase)<{ variant?: 'light' | 'dark' }>(({ variant = 'light' }) => {
  const isDark = variant === 'dark';
  return {
    '& .MuiInputBase-input': {
      borderRadius: '1rem',
      border: `1px solid ${isDark ? '#333F66' : '#DDE1EE'}`,
      backgroundColor: isDark ? '#333F66' : 'white',
      padding: '4px',
      height: '48px',
      width: '100%',
      fontFamily: 'Urbanist',
      fontSize: '24px',
      fontWeight: 700,
      color: isDark ? '#EEF0F6' : '#333F66',
      textAlign: 'center',
      lineHeight: '32px',
    },
    '& .MuiInputBase-input:focus': {
      borderColor: '#2F5DF5',
    },
  };
});

const DEFAULT_VALIDATE_PASTE = (text: string, length: number) =>
  /^\d*$/.test(text) && text.length === length;

interface CodeInputProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  onComplete?: (code: string) => void;
  /** Only allow paste to fill the input when the pasted content is a valid code. Default: exactly `length` digits. */
  validatePaste?: (text: string) => boolean;
  /** Visual variant; use 'dark' on dark backgrounds (e.g. modals). */
  variant?: 'light' | 'dark';
}

const makeStyles = () => ({
  container: {
    display: 'flex',
    gap: '16px',
    width: '100%',
  },
  input: {
    flex: '1 0 0',
    minWidth: 0,
  },
});

export function CodeInput({
  value,
  onChange,
  length = 6,
  onComplete,
  validatePaste,
  variant = 'light',
}: CodeInputProps) {
  const sx = makeStyles();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const isValidPaste = validatePaste ?? ((text: string) => DEFAULT_VALIDATE_PASTE(text, length));

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleChange = (index: number, newValue: string) => {
    // Only allow numbers
    const numericValue = newValue.replace(/[^0-9]/g, '');

    if (numericValue.length > 1) {
      // Handle paste
      const pastedValues = numericValue.slice(0, length).split('');
      const newCode = [...value.split('')];

      pastedValues.forEach((val, i) => {
        if (index + i < length) {
          newCode[index + i] = val;
        }
      });

      const finalCode = newCode.join('').slice(0, length);
      onChange(finalCode);

      // Focus next empty input or last input
      const nextIndex = Math.min(index + pastedValues.length, length - 1);
      if (inputRefs.current[nextIndex]) {
        inputRefs.current[nextIndex].focus();
      }

      if (finalCode.length === length && onComplete) {
        onComplete(finalCode);
      }
      return;
    }

    // Single digit input
    const newCode = value.split('');
    newCode[index] = numericValue;
    const finalCode = newCode.join('').slice(0, length);
    onChange(finalCode);

    // Auto-advance to next input
    if (numericValue && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Call onComplete when all digits are filled
    if (finalCode.length === length && onComplete) {
      onComplete(finalCode);
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const raw = e.clipboardData.getData('text');
    const pastedData = raw.replace(/[^0-9]/g, '');
    if (pastedData.length > 0 && isValidPaste(pastedData)) {
      const firstEmptyIndex = value.split('').findIndex((char) => !char);
      const startIndex = firstEmptyIndex >= 0 ? firstEmptyIndex : 0;
      handleChange(startIndex, pastedData);
    }
  };

  return (
    <Box sx={sx.container}>
      {Array.from({ length }).map((_, index) => (
        <Box key={`code-input-${index + 1}`} sx={sx.input}>
          <CodeInputBase
            variant={variant}
            inputRef={(el) => {
              inputRefs.current[index] = el;
            }}
            value={value[index] || ''}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            inputProps={{
              maxLength: 1,
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
          />
        </Box>
      ))}
    </Box>
  );
}
